import { config } from 'config';
import { clientConfig, handleResponse } from '~/lib/api';
import { tasksHc } from '#/modules/tasks/hc';

// Create Hono clients to make requests to the backend
export const client = tasksHc(config.backendUrl, clientConfig);

type CreateTaskParams = Parameters<(typeof client.index)['$post']>['0']['json'] & {
  organizationId: string;
};

// Create a new task
export const createTask = async ({ organizationId, ...task }: CreateTaskParams) => {
  const response = await client.index.$post({
    param: { orgIdOrSlug: organizationId },
    json: task,
  });
  const json = await handleResponse(response);
  return json.data;
};

export type GetTasksParams = Omit<Parameters<(typeof client.index)['$get']>['0']['query'], 'limit' | 'offset'> & {
  orgIdOrSlug: string;
  limit?: number;
  offset?: number;
  page?: number;
};

// Get list of tasks
export const getTasksList = async (
  {
    q,
    sort = 'status',
    order = 'desc',
    page = 0,
    acceptedCutOff,
    limit = config.requestLimits.tasks,
    offset,
    projectId,
    orgIdOrSlug,
    status,
  }: GetTasksParams,
  signal?: AbortSignal,
) => {
  const response = await client.index.$get(
    {
      param: { orgIdOrSlug },
      query: {
        q,
        sort,
        order,
        projectId,
        status,
        acceptedCutOff,
        offset: typeof offset === 'number' ? String(offset) : String(page * limit),
        limit: String(limit),
      },
    },
    {
      fetch: (input: RequestInfo | URL, init?: RequestInit) => {
        return fetch(input, {
          ...init,
          credentials: 'include',
          signal,
        });
      },
    },
  );

  const json = await handleResponse(response);
  return json.data;
};

export type GetTaskParams = Parameters<(typeof client)[':id']['$get']>['0']['param'];

// Get task by its ID
export const getTask = async ({ id, orgIdOrSlug }: GetTaskParams) => {
  const response = await client[':id'].$get({ param: { id, orgIdOrSlug } });

  const json = await handleResponse(response);
  return json.data;
};

export type UpdateTaskParams = Parameters<(typeof client)[':id']['$put']>['0']['json'] & {
  id: string;
  orgIdOrSlug: string;
};

// Update task by its ID
export const updateTask = async ({ id, orgIdOrSlug, key, data, order }: UpdateTaskParams) => {
  const newOrder = typeof order === 'number' ? order : null;
  const response = await client[':id'].$put({
    param: { id, orgIdOrSlug },
    json: {
      key,
      data,
      order: newOrder,
    },
  });

  const json = await handleResponse(response);
  return json.data;
};

export type DeleteTaskParams = Parameters<(typeof client.index)['$delete']>['0']['param'] & { ids: string[] };

// Delete tasks
export const deleteTasks = async ({ ids, orgIdOrSlug }: DeleteTaskParams) => {
  const response = await client.index.$delete({
    param: { orgIdOrSlug },
    json: { ids },
  });
  const json = await handleResponse(response);
  return json.success;
};

// Import tasks
export const importTasks = async ({ organizationId, projectId, file }: { organizationId: string; projectId: string; file: File }) => {
  const response = await client.import[':projectId'].$post({
    param: { orgIdOrSlug: organizationId, projectId },
    form: { file },
  });
  const json = await handleResponse(response);
  return json.data;
};
